/**
 * Two-series overlaid hexagonal radar for comparing two analysis snapshots.
 * Baseline = dashed grey polygon; Compare = solid lavender polygon + vertex dots.
 * Pure SVG, presentational — no Recharts dependency.
 */

interface CompareRadarProps {
	baseline: number[];
	compare: number[];
	labels: string[];
	size?: number;
}

export function CompareRadar({ baseline, compare, labels, size = 240 }: CompareRadarProps) {
	const cx = size / 2;
	const cy = size / 2;
	const r = size / 2 - 30;
	const N = 6;
	const max = 10;

	const pt = (i: number, v: number): [number, number] => {
		const ang = (i / N) * Math.PI * 2 - Math.PI / 2;
		const rr = (v / max) * r;
		return [cx + Math.cos(ang) * rr, cy + Math.sin(ang) * rr];
	};

	const rings = [0.25, 0.5, 0.75, 1] as const;

	const polyPoints = (vals: number[]) => vals.map((v, i) => pt(i, v).join(",")).join(" ");

	return (
		<svg viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size }} aria-hidden="true">
			{/* Concentric grid rings */}
			{rings.map((m) => (
				<polygon
					key={m}
					points={labels.map((_, j) => pt(j, max * m).join(",")).join(" ")}
					fill="none"
					stroke="var(--color-hairline)"
					strokeWidth={1}
				/>
			))}

			{/* Spokes */}
			{labels.map((label, i) => {
				const [x2, y2] = pt(i, max);
				return (
					<line
						key={label}
						x1={cx}
						y1={cy}
						x2={x2}
						y2={y2}
						stroke="var(--color-hairline)"
						strokeWidth={1}
					/>
				);
			})}

			{/* Baseline polygon — dashed grey */}
			<polygon
				points={polyPoints(baseline)}
				fill="var(--color-ink-tertiary)"
				fillOpacity="0.12"
				stroke="var(--color-ink-tertiary)"
				strokeWidth={1.25}
				strokeDasharray="3 3"
			/>

			{/* Compare polygon — solid lavender */}
			<polygon
				points={polyPoints(compare)}
				fill="var(--color-primary)"
				fillOpacity="0.2"
				stroke="var(--color-primary)"
				strokeWidth={1.5}
			/>

			{/* Compare vertex dots */}
			{compare.map((v, i) => {
				const [px, py] = pt(i, v);
				// biome-ignore lint/suspicious/noArrayIndexKey: positional series
				return <circle key={i} cx={px} cy={py} r={2.5} fill="var(--color-primary)" />;
			})}

			{/* Axis labels */}
			{labels.map((label, i) => {
				const ang = (i / N) * Math.PI * 2 - Math.PI / 2;
				const lx = cx + Math.cos(ang) * (r + 16);
				const ly = cy + Math.sin(ang) * (r + 16);
				return (
					<text
						key={label}
						x={lx}
						y={ly}
						textAnchor="middle"
						dominantBaseline="middle"
						fontSize="9"
						fill="var(--color-ink-subtle)"
						letterSpacing="0.06em"
						style={{ textTransform: "uppercase" }}
					>
						{label}
					</text>
				);
			})}
		</svg>
	);
}
